import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Popover from '@mui/material/Popover';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import { ChevronDown, FolderKanban, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import AppLoader from '../../../utils/AppLoader';

export default function HeaderProjectSelect() {
  const apiurl = localStorage.getItem('apiurl');
  const token = localStorage.getItem('dbTokenData');
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(localStorage.getItem('projectId') || '');
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchProjects = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${apiurl}project`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.data?.cd === 1) {
          toast.error(response.data.description);
        } else {
          const mappedProjects = response.data.map((project) => ({
            value: project.code,
            label: project.name
          }));

          setProjects(mappedProjects);
        }
      } catch (error) {
        toast.error('Some Issue Please Check Network Connection.', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [apiurl, token]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleProjectSelect = (projectValue) => {
    setSelectedProject(projectValue);
    localStorage.setItem('projectId', projectValue);
    handleClose();
    window.location.reload();
  };

  const open = Boolean(anchorEl);
  const selectedProjectLabel = projects.find((p) => p.value === selectedProject)?.label || 'Select Project';

  if (loading) {
    return <AppLoader />;
  }

  return (
    <Box sx={{ width: '100%', ml: { xs: 0, md: 1 } }}>
      <Box
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1,
          borderRadius: 2,
          cursor: 'pointer',
          transition: 'all 0.2s',
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          width: { xs: '100%', md: 200 },
          '&:hover': {
            bgcolor: 'action.hover',
            borderColor: 'primary.main',
            transform: 'translateY(-1px)',
            boxShadow: 1
          }
        }}
      >
        <FolderKanban size={18} />
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            maxWidth: 180,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {selectedProjectLabel}
        </Typography>
        <ChevronDown
          size={16}
          style={{
            transition: 'transform 0.2s',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)'
          }}
        />
      </Box>

      {/* Popover menu */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        sx={{
          mt: 1
        }}
        PaperProps={{
          sx: {
            width: 280,
            maxHeight: 400,
            borderRadius: 2,
            boxShadow: 3
          }
        }}
      >
        <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            SWITCH PROJECT
          </Typography>
        </Box>
        <List sx={{ p: 1 }}>
          {projects.length === 0 ? (
            <ListItem disablePadding>
              <ListItemButton disabled sx={{ borderRadius: 1 }}>
                <ListItemText
                  primary="No projects to switch"
                  primaryTypographyProps={{
                    variant: 'body2',
                    color: 'text.secondary',
                    fontStyle: 'italic'
                  }}
                />
              </ListItemButton>
            </ListItem>
          ) : (
            projects.map((project) => (
              <ListItem key={project.value} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => handleProjectSelect(project.value)}
                  selected={selectedProject === project.value}
                  sx={{
                    borderRadius: 1,
                    '&.Mui-selected': {
                      bgcolor: 'primary.lighter',
                      '&:hover': {
                        bgcolor: 'primary.light'
                      }
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
                    <FolderKanban size={16} />
                    <ListItemText
                      primary={project.label}
                      primaryTypographyProps={{
                        variant: 'body2',
                        fontWeight: selectedProject === project.value ? 600 : 400
                      }}
                    />
                    {selectedProject === project.value && <Check size={16} color="#1976d2" />}
                  </Box>
                </ListItemButton>
              </ListItem>
            ))
          )}
        </List>
      </Popover>
    </Box>
  );
}
